import { ArtistDTO, Song } from "../../view/spotipitan/types";
import ImageMosaic from "../image-mosaic/image-mosaic";
import { handleCopy, parseTimeToString, shuffleList } from "../../../utils/utils";
import './artist-header.scss';
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { deleteAlbum, getAlbumMotionArtwork, normalizeFileUrl, Spotip17anFunctions } from "../../../utils/api";
import { AlbumMotionArtworkResponse } from "../../../utils/apiTypes";
import SquareImage from "../square-image/square-image";
import userState from "../../../store/UserState";
import { useNavigate } from "react-router-dom";
import ConfirmationModal from "../confirmation-modal/confirmation-modal";
import useSongStore from "../../view/spotipitan/store/SongState";
import { isPlaylistInDB } from "../../../utils/dbUtils";
import { useScreenSize } from "../../../hooks";

interface Props {
    artist?: ArtistDTO;
    loading: boolean;
    isPlaylist?: boolean;
    isAlbum?: boolean;
    creatorName?: string;
    onClickCreator?: () => void;
    handleClickPlay?: (songs: Song[]) => void;
    isOwner?: boolean;
    handleClickSaveOffline?: () => void;
    saving?: boolean;
    summaryHeader?: boolean;
    summarySize?: number;
    onClickImage?: () => void;
    onEditAlbum?: () => void;
    onDeleteAlbum?: () => void;
}

const ArtistHeader = ({
    artist,
    loading,
    handleClickPlay,
    isPlaylist,
    isAlbum,
    creatorName,
    onClickCreator,
    isOwner,
    handleClickSaveOffline,
    saving,
    summaryHeader,
    summarySize,
    onClickImage,
    onEditAlbum,
    onDeleteAlbum
}: Props) => {

    const [isSaved, setIsSaved] = useState(false);
    const [isSavedOffline, setIsSavedOffline] = useState(false);
    const { user, offlineMode } = userState(state => state.state);
    const { setOriginalQueueOrder, setLoopMode, setRandomQueue } = useSongStore(state => state);
    const [loadingSave, setLoadingSave] = useState(true);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [loadingOffline, setLoadingOffline] = useState(false);
    const [motionArtwork, setMotionArtwork] = useState<AlbumMotionArtworkResponse | null>(null);
    const [videoFailed, setVideoFailed] = useState(false);
    const [isTitleOverflowing, setIsTitleOverflowing] = useState(false);
    const [marqueeDuration, setMarqueeDuration] = useState(15);
    const titleContainerRef = useRef<HTMLDivElement | null>(null);
    const titleTextRef = useRef<HTMLSpanElement | null>(null);
    const navigate = useNavigate();
    const { width } = useScreenSize();

    useEffect(() => {
        const checkOverflow = () => {
            if (!titleContainerRef.current || !titleTextRef.current) return;
            const containerWidth = titleContainerRef.current.clientWidth;
            const textWidth = titleTextRef.current.scrollWidth;
            const overflowing = textWidth > containerWidth + 2;
            setIsTitleOverflowing(overflowing);
            if (overflowing) {
                const speed = 40; // ~40px per second
                const fullWidth = textWidth + 48; // includes 48px gap
                setMarqueeDuration(Math.max(10, Math.round(fullWidth / speed)));
            }
        };

        const timeout = setTimeout(checkOverflow, 50);

        if (document.fonts?.ready) {
            document.fonts.ready.then(checkOverflow);
        }

        let resizeObserver: ResizeObserver | null = null;
        if (titleContainerRef.current && typeof ResizeObserver !== "undefined") {
            resizeObserver = new ResizeObserver(() => {
                checkOverflow();
            });
            resizeObserver.observe(titleContainerRef.current);
        }

        window.addEventListener("resize", checkOverflow);
        return () => {
            clearTimeout(timeout);
            resizeObserver?.disconnect();
            window.removeEventListener("resize", checkOverflow);
        };
    }, [artist?.username]);

    useEffect(() => {
        if (!artist || offlineMode) {
            setMotionArtwork(null);
            return;
        }

        let isMounted = true;
        setVideoFailed(false);

        // 1. Direct optimization: If album already has native motion_cover_url, use it directly without network call
        if (artist.motion_cover_url) {
            const motionUrl = normalizeFileUrl(artist.motion_cover_url);
            setMotionArtwork({
                error: false,
                hasMotionArtwork: true,
                videoUrl: motionUrl,
                videoMp4Url: motionUrl,
                tallVideoMp4Url: motionUrl,
                previewFrameUrl: normalizeFileUrl(artist.poster_url || artist.cover_url),
                albumName: artist.username || artist.title,
                artistName: creatorName || artist.creator_name,
            });
            return;
        }

        // 2. Fallback: Only for external albums without native motion_cover_url
        const fetchMotion = async () => {
            const albumName = artist.username;
            const artistName = creatorName || artist.creator_name || artist.songs?.[0]?.user?.username;

            if (!albumName) return;

            try {
                const res = await getAlbumMotionArtwork({
                    albumName,
                    artistName,
                });
                if (isMounted && res && res.hasMotionArtwork && (res.videoMp4Url || res.tallVideoMp4Url || res.videoUrl)) {
                    setMotionArtwork(res);
                }
            } catch {
                // Silently fallback if unavailable
            }
        };

        fetchMotion();

        return () => {
            isMounted = false;
        };
    }, [artist?.id, artist?.username, artist?.motion_cover_url, artist?.poster_url, artist?.cover_url, creatorName, artist?.creator_name, offlineMode]);

    useEffect(() => {
        if (!artist?.id || offlineMode) {
            return;
        }

        setLoadingOffline(true);

        isPlaylistInDB(artist.id + "").then((res) => {
            setIsSavedOffline(res);
            setLoadingOffline(false);
        });

    }, [saving, artist, offlineMode]);

    useEffect(() => {
        if (!isPlaylist || isOwner || !artist?.id || !user?.uid) {
            setLoadingSave(false);
            return;
        }

        Spotip17anFunctions.use('checkPlaylistSubscription').call({ playlistId: artist.id + "" }).then((res: {
            subscribed: boolean
        }) => {
            setIsSaved(res.subscribed);
            setLoadingSave(false);
        });
    }, [artist, isPlaylist, isOwner, user]);

    if (loading) {
        return (
            <div className="artist-header artist-header--loading">
                <div className="artist-header__loading-skeleton" />
            </div>
        );
    }

    if (!artist) {
        return null;
    }

    const getTotalTimeSongs = () => {
        let total = 0;
        artist.songs?.forEach(song => {
            total += parseInt(song.duration + "");
        });
        return total;
    };

    const handleClickSave = (value: boolean) => {
        if (loadingSave) {
            return;
        }

        if (!user?.uid) {
            toast.error('Debes iniciar sesión para guardar la playlist');
            return;
        }

        setIsSaved(value);
        setLoadingSave(true);

        Spotip17anFunctions.use(value ? 'subscribePlaylist' : 'unsubscribePlaylist').call({ playlistId: artist.id + "" }).then(() => {
            setLoadingSave(false);
            if (value) {
                toast.success('Has guardado la playlist');
            } else {
                toast.error('Has eliminado la playlist');
            }
        });
    };

    const handleClickPlayNormal = () => {
        if (!handleClickPlay || !artist.songs?.length) return;

        setLoopMode('none');
        setRandomQueue(false);
        setOriginalQueueOrder(artist.songs.map(x => x.id));
        handleClickPlay(artist.songs);
    };

    const handleClickShuffle = () => {
        if (!handleClickPlay || !artist.songs?.length) return;

        setLoopMode('none');
        setRandomQueue(true);
        setOriginalQueueOrder(artist.songs.map(x => x.id));
        handleClickPlay(shuffleList(artist.songs));
    };

    const handleShare = () => {
        let shareUrl = window.location.href;
        const playlistIdStr = String(artist.id || "");
        if (playlistIdStr === "favs" && (user?.uid || artist.creator_uid)) {
            const uid = user?.uid || artist.creator_uid;
            shareUrl = `${window.location.origin}/playlist/favs-${uid}`;
        }
        handleCopy(shareUrl);
    };

    if (summaryHeader) {
        return (
            <div className="artist-header__summary">
                <div className="artist-header__summary_left">
                    <div
                        className="artist-header__summary__image"
                        onClick={onClickImage}
                        style={{ cursor: onClickImage ? "pointer" : "default" }}
                    >
                        {isAlbum || artist.poster_url || artist.songs?.length === 1 ? (
                            <SquareImage
                                playlist={artist}
                                song={artist.poster_url ? undefined : artist.songs?.[0]}
                                style={{ width: summarySize || 48, height: summarySize || 48 }}
                            />
                        ) : (
                            <div style={{ width: summarySize || 48, height: summarySize || 48 }}>
                                <ImageMosaic songs={artist.songs?.slice(0, 4) || []} />
                            </div>
                        )}
                    </div>
                    <div className="artist-header__summary__info">
                        <div className="artist-header__summary__info__name">
                            {artist.username}
                        </div>
                        <div className="artist-header__summary__info__type text-muted">
                            <small>
                                {isAlbum ? "Álbum • " : isPlaylist ? "Playlist • " : ""}
                                {artist.songs?.length || 0} canciones
                            </small>
                        </div>
                    </div>
                </div>
                <div className="artist-header__summary_actions">
                    {!!artist.songs?.length && handleClickPlay && (
                        <>
                            <i
                                className="fa-solid fa-shuffle"
                                onClick={handleClickShuffle}
                                title="Modo aleatorio"
                            />
                            <i
                                className="fa-solid fa-circle-play artist-header__play-btn artist-header__play-btn--sm"
                                onClick={handleClickPlayNormal}
                                title="Reproducir"
                            />
                        </>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="artist-header">
            <div className="artist-header__hero">
                {/* Background Hero Media with Apple Music style bottom fade */}
                <div className="artist-header__hero-media">
                    {motionArtwork && !videoFailed && (motionArtwork.tallVideoMp4Url || motionArtwork.videoMp4Url || motionArtwork.videoUrl) ? (
                        <video
                            className="artist-header__hero-video"
                            src={motionArtwork.tallVideoMp4Url || motionArtwork.videoMp4Url || motionArtwork.videoUrl || undefined}
                            poster={motionArtwork.previewFrameUrl || normalizeFileUrl(artist.poster_url)}
                            autoPlay
                            loop
                            muted
                            playsInline
                            onError={() => setVideoFailed(true)}
                        />
                    ) : isAlbum || artist.poster_url || artist.songs?.length === 1 ? (
                        <SquareImage
                            playlist={artist}
                            song={artist.poster_url ? undefined : artist.songs?.[0]}
                            big
                            fill
                            className="artist-header__hero-img"
                        />
                    ) : (
                        <ImageMosaic songs={artist.songs?.slice(0, 4) || []} />
                    )}
                    <div className="artist-header__hero-gradient" />
                </div>

                {/* Desktop dedicated cover (shown on >= 769px) */}
                <div className="artist-header__desktop-cover" onClick={onClickImage} role={onClickImage ? "button" : undefined}>
                    {motionArtwork?.videoMp4Url && !videoFailed ? (
                        <video
                            className="artist-header__desktop-video"
                            src={motionArtwork.videoMp4Url}
                            poster={motionArtwork.previewFrameUrl || normalizeFileUrl(artist.poster_url)}
                            autoPlay
                            loop
                            muted
                            playsInline
                            onError={() => setVideoFailed(true)}
                        />
                    ) : isAlbum || artist.poster_url || artist.songs?.length === 1 ? (
                        <SquareImage
                            playlist={artist}
                            song={artist.poster_url ? undefined : artist.songs?.[0]}
                            big
                            fill
                        />
                    ) : (
                        <ImageMosaic songs={artist.songs?.slice(0, 4) || []} />
                    )}
                </div>

                {/* Hero Content (Title, Meta & Controls) */}
                <div className="artist-header__hero-content">
                    <div className="artist-header__badge-container">
                        {isAlbum ? (
                            <span className="artist-header__badge artist-header__badge--album">{artist.songs?.length == 1 ? "Single" : "Álbum"}</span>
                        ) : isPlaylist ? (
                            <span className="artist-header__badge artist-header__badge--playlist">Playlist</span>
                        ) : null}
                    </div>

                    <div
                        className={`artist-header__title-container ${isTitleOverflowing ? "artist-header__title-container--overflowing" : ""}`}
                        ref={titleContainerRef}
                    >
                        <div
                            className={`artist-header__title-track ${isTitleOverflowing ? "artist-header__title-track--animating" : ""}`}
                            style={
                                isTitleOverflowing
                                    ? ({
                                        "--marquee-duration": `${marqueeDuration}s`,
                                        animationDuration: `${marqueeDuration}s`,
                                        WebkitAnimationDuration: `${marqueeDuration}s`
                                    } as React.CSSProperties)
                                    : undefined
                            }
                        >
                            <h1 className="artist-header__title">
                                <span className="artist-header__title-item">
                                    <span className="artist-header__title-text" ref={titleTextRef}>
                                        {artist.username}
                                    </span>
                                </span>
                                {isTitleOverflowing && (
                                    <span className="artist-header__title-item artist-header__title-item--dup" aria-hidden="true">
                                        <span className="artist-header__title-text">
                                            {artist.username}
                                        </span>
                                    </span>
                                )}
                            </h1>
                        </div>
                        {artist.description && width > 1200 && <div className="artist-header__title-container--description">
                            {artist.description}
                        </div>}
                    </div>

                    <div className="artist-header__meta">
                        {(isAlbum || (isPlaylist && (creatorName || artist.creator_name))) && (
                            <>
                                <span className="artist-header__creator">
                                    <span className="artist-header__creator-prefix">De </span>
                                    <span
                                        role={onClickCreator ? "button" : undefined}
                                        className={onClickCreator ? "artist-header__creator-link" : "fw-bold"}
                                        onClick={onClickCreator}
                                    >
                                        {creatorName || artist.creator_name}
                                    </span>
                                </span>
                                <span className="artist-header__meta-sep">•</span>
                            </>
                        )}
                        <span className="artist-header__stats">
                            {artist.songs?.length == 1 ? "Single" : `${artist.songs?.length || 0} canciones`}
                        </span>
                        {!!artist.songs?.length && (
                            <>
                                <span className="artist-header__meta-sep">•</span>
                                <span className="artist-header__duration">{parseTimeToString(getTotalTimeSongs())}</span>
                            </>
                        )}
                    </div>

                    {/* Action controls styled like artist page */}
                    <div className="artist-header__actions">
                        {isOwner && onEditAlbum && (
                            <i
                                className="fa-solid fa-pen-to-square artist-header__edit-btn"
                                onClick={onEditAlbum}
                                title={isAlbum ? "Editar álbum" : "Editar"}
                            />
                        )}

                        {(artist.id === "favs" || String(artist.id).startsWith("favs-")) && isOwner ? (
                            <i
                                className="fa-solid fa-heart artist-header__fav-badge-btn"
                                title="Tus canciones favoritas"
                            />
                        ) : isOwner ? (
                            !isAlbum && (
                                <i
                                    className="fa-solid fa-trash artist-header__delete-btn"
                                    onClick={() => setShowConfirmDelete(true)}
                                    title="Eliminar playlist"
                                />
                            )
                        ) : (
                            isPlaylist && !offlineMode && (
                                <i
                                    className={isSaved ? "fa-solid fa-check text-success" : "fa-solid fa-plus"}
                                    onClick={() => handleClickSave(!isSaved)}
                                    title={isSaved ? "Guardado en biblioteca" : "Guardar en biblioteca"}
                                />
                            )
                        )}

                        {!offlineMode && (
                            saving || loadingOffline ? (
                                <i className="fa-solid fa-spinner fa-spin" />
                            ) : !isSavedOffline ? (
                                <i
                                    className="fa-regular fa-circle-down"
                                    onClick={handleClickSaveOffline}
                                    title="Descargar para offline"
                                />
                            ) : (
                                <i
                                    className="fa-solid fa-circle-check text-success"
                                    title="Descargado offline"
                                />
                            )
                        )}

                        {!!artist.songs?.length && handleClickPlay && (
                            <i
                                className="fa-solid fa-circle-play artist-header__play-btn"
                                onClick={handleClickPlayNormal}
                                title="Reproducir"
                            />
                        )}

                        {!!artist.songs?.length && handleClickPlay && (
                            <i
                                className="fa-solid fa-shuffle"
                                onClick={handleClickShuffle}
                                title="Modo aleatorio"
                            />
                        )}

                        <i
                            className="fa-solid fa-share-nodes"
                            onClick={handleShare}
                            title="Compartir enlace"
                        />
                    </div>
                </div>
                {artist.description && width <= 1200 && <div className="artist-header__title-container--description text-center w-100"
                    style={{
                        bottom: 0
                    }}>
                    {artist.description}
                </div>}
            </div>

            <ConfirmationModal
                show={showConfirmDelete}
                onClose={() => setShowConfirmDelete(false)}
                onConfirm={() => {
                    if (onDeleteAlbum) {
                        onDeleteAlbum();
                        setShowConfirmDelete(false);
                        return;
                    }

                    setLoadingDelete(true);
                    if (isAlbum) {
                        deleteAlbum(artist.id + "").then(() => {
                            toast.success('Álbum eliminado');
                            navigate('/my-library');
                        }).catch(() => {
                            toast.error('Error al eliminar el álbum');
                        }).finally(() => {
                            setLoadingDelete(false);
                            setShowConfirmDelete(false);
                        });
                    } else {
                        Spotip17anFunctions.use('deletePlaylist').call({ playlistId: artist.id + "" }).then(() => {
                            toast.success('Playlist eliminada');
                            navigate('/');
                        }).catch(() => {
                            toast.error('Error al eliminar la playlist');
                        }).finally(() => {
                            setLoadingDelete(false);
                            setShowConfirmDelete(false);
                        });
                    }
                }}
                title={isAlbum ? "Eliminar álbum" : "Eliminar playlist"}
                message={isAlbum ? "¿Estás seguro? Esto eliminará de la existencia este álbum y sus relaciones de pistas." : "¿Estas segure? Esto eliminará de la existencia esta gran playlist."}
                confirmText="Eliminar"
                cancelText="Cancelar"
                loading={loadingDelete}
            />
        </div>
    );
};

export default ArtistHeader;
